import * as React from 'react';
import { Button } from '../../components/ui/button';
import { rem } from '../../lib/rem';

type UnmatchedFolder = {
  folderPath: string;
  folderName: string;
  projectNumber: string;
  namePart: string;
  include: boolean;
  editedName: string;
};

interface DriveImportModalProps {
  unmatchedFolders: UnmatchedFolder[];
  setUnmatchedFolders: React.Dispatch<React.SetStateAction<UnmatchedFolder[]>>;
  importingFolders: boolean;
  setShowImportModal: React.Dispatch<React.SetStateAction<boolean>>;
  importUnmatchedFolders: () => void;
}

export function DriveImportModal({
  unmatchedFolders,
  setUnmatchedFolders,
  importingFolders,
  setShowImportModal,
  importUnmatchedFolders,
}: DriveImportModalProps) {
  return (
    <div
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !importingFolders) setShowImportModal(false);
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: 'var(--sf)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--bd)',
          padding: 'var(--space-5)',
          width: rem(640),
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,.2)',
        }}
      >
        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 'var(--font-size-lg)',
              marginBottom: 'var(--space-1)',
            }}
          >
            Import folders as projects
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-base)',
              color: 'var(--tm)',
              marginBottom: 'var(--space-4)',
            }}
          >
            Found {unmatchedFolders.length} folder{unmatchedFolders.length === 1 ? '' : 's'} on the
            drive whose project number doesn't exist in the app yet. Pick which ones to create.
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
            <button
              onClick={() =>
                setUnmatchedFolders(unmatchedFolders.map((u) => ({ ...u, include: true })))
              }
              style={{
                padding: `var(--space-1) ${rem(10)}`,
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--bd)',
                background: 'transparent',
                color: 'var(--tm)',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
              }}
            >
              Select all
            </button>
            <button
              onClick={() =>
                setUnmatchedFolders(unmatchedFolders.map((u) => ({ ...u, include: false })))
              }
              style={{
                padding: `var(--space-1) ${rem(10)}`,
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--bd)',
                background: 'transparent',
                color: 'var(--tm)',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
              }}
            >
              Clear all
            </button>
            <span
              style={{
                marginLeft: 'auto',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--tm)',
                alignSelf: 'center',
              }}
            >
              {unmatchedFolders.filter((u) => u.include).length} selected
            </span>
          </div>
        </div>
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            border: '1px solid var(--bd)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--ib)',
          }}
        >
          {unmatchedFolders.map((u, i) => (
            <label
              key={u.folderPath}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: rem(10),
                padding: `${rem(8)} var(--space-3)`,
                borderBottom: i < unmatchedFolders.length - 1 ? '1px solid var(--bd)' : 'none',
                cursor: 'pointer',
                background: u.include ? 'rgba(var(--acr),0.04)' : 'transparent',
              }}
            >
              <input
                type="checkbox"
                checked={u.include}
                onChange={(e) =>
                  setUnmatchedFolders((prev) =>
                    prev.map((x, idx) => (idx === i ? { ...x, include: e.target.checked } : x))
                  )
                }
                style={{ flexShrink: 0, cursor: 'pointer' }}
              />
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 700,
                  color: 'var(--ac)',
                  minWidth: rem(80),
                }}
              >
                {u.projectNumber}
              </span>
              <input
                value={u.editedName}
                onChange={(e) =>
                  setUnmatchedFolders((prev) =>
                    prev.map((x, idx) => (idx === i ? { ...x, editedName: e.target.value } : x))
                  )
                }
                placeholder="Project name"
                style={{
                  flex: 1,
                  padding: `var(--space-1) ${rem(8)}`,
                  border: '1px solid var(--bd)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--sf)',
                  color: 'var(--tx)',
                  fontSize: 'var(--font-size-base)',
                }}
              />
              <span
                title={u.folderPath}
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--tm)',
                  fontFamily: "'JetBrains Mono', monospace",
                  maxWidth: rem(160),
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {u.folderPath.split(/[\\/]/).slice(-2).join('/')}
              </span>
            </label>
          ))}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-4)',
            flexShrink: 0,
          }}
        >
          <Button
            variant="outline"
            onClick={() => {
              setShowImportModal(false);
              setUnmatchedFolders([]);
            }}
            disabled={importingFolders}
          >
            Cancel
          </Button>
          <Button
            onClick={importUnmatchedFolders}
            disabled={importingFolders || unmatchedFolders.filter((u) => u.include).length === 0}
          >
            {importingFolders
              ? 'Importing…'
              : `Import ${unmatchedFolders.filter((u) => u.include).length}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
